import { Fragment, useMemo } from 'react';
import styled from 'styled-components';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import { solfegeLevelParams } from '../../../../../common/src/sing_in_solfege.ts';
import MicIcon from '../../../assets/learn-mic.svg?react';
import SpeakerIcon from '../../../assets/learn-speaker.svg?react';
import { ButtonColumn, SecondaryButton, ToggleButton } from '../learn_buttons.tsx';
import { CueRow, CueSlack } from '../learn_cue_row.tsx';
import { HearingYouMeter } from '../learn_meter.tsx';
import { attemptPips, ProgressPipsRow, ScreenBody, ScreenFoot, shortScreen, Slack } from '../learn_screen_parts.tsx';
import { pitchHintAmber } from '../pitch/pitch_colors.ts';
import { solfegeResultColorCss } from './solfege_colors.ts';
import {
  currentSolfegePhrase,
  isSolfegeHearingGuide,
  isSolfegeHearingMine,
  isSolfegePhrasePlaying,
  isSolfegeResultSettling,
  solfegeCountInBeatsLeft,
  solfegeCueNow,
  solfegeHint,
  solfegeHintText,
  solfegeHomeLine,
  solfegeLiveDot,
  solfegeMicLevel,
  solfegeNoteDisplays,
  solfegePhraseCount,
  solfegePhraseIndex,
  solfegePlaybackIntoMs,
  solfegeResultCountText,
  solfegeResultSentenceParts,
  SolfegeSessionState,
  SolfegeTextPart,
  SolfegeTraceSample,
  solfegeTraceSamples,
} from './solfege_session.ts';
import { SolfegeStaff, useSolfegeStaffGeometry } from './solfege_staff.tsx';
import { solfegeTraceJoin, solfegeTraceLead, solfegeTraceLine } from './solfege_trace.ts';

// One phrase of a Sing in Solfège session (issue #3722), from the "Sing in Solfège (listen)",
// "(sing along)" and "(result)" artboards. The three phases a phrase goes through share one screen,
// as Interval Training's pattern screen does, so the progress row, the hymn's title, the phrase's
// label, the prompt line and the staff stay put while what is under them changes:
//
//   - Listen: "Listen and follow the syllables"; under the staff "Playing the phrase" while it
//     plays; then the line about do, the home note, and where the phrase starts.
//   - Sing: "Sing along" (the guide playing softly under the singer) or "Your turn" (guide-first),
//     with the count-in's beats left beside it until the phrase starts; the dot moving through the
//     phrase drawing the singer's line; under the staff what the guide is doing and the live hint;
//     then, in the slack above the "Hearing you" meter, Ready · Set · Go! pulsing large on the
//     count-in's last two beats and the take's first. In the count-in, a singer humming the first
//     note once the starting pitch has sounded gets the dot at the first note's start and the hint
//     beside "Listen for your first note", so they can find the note before it counts.
//   - Result: "8 of 9 notes", with "Hear guide" at the right end of that line; the staff in its
//     result colours with the line still drawn; the one sentence about the miss most worth fixing
//     and the legend.
//
// The foot's first button is one element from listen into sing - "Sing it with me" or "Sing it"
// (solid), then "Start over" (outlined) - so the tap that starts the count-in is never swapped for
// another element under the finger (the ToggleButton rule in learn_buttons.tsx, issue #3518). The
// result's "Next phrase" or "See your score" (solid) is a different element, though: the take ends
// on the clock, not on a tap, and a press that began on "Start over" as it ended must not finish on
// "Next phrase" and file the take. For the result's first moments (solfegeResultGuardMs) its buttons
// are disabled, as the session ignores them then. Under the main button, "Play again" on the listen
// screen, and on the result "Hear mine" (which reads "Stop" while the take plays back, and is greyed
// out when the page has no recording to play) beside "Sing again".
//
// "Hear guide" (#3722, round 2) plays the phrase again on the result, in either guide mode, without
// adding a fourth big button: a compact pill at the right end of the "N of M notes" line, which was
// empty, with the speaker icon. It reads "Stop" while the phrase plays, and a playhead crosses the
// staff as it does for Hear mine. Its tap target is 44 px high, though the pill drawn is smaller and
// the line keeps its height, so the staff does not move between the sing screen and the result. The
// result's guard disables it with the other buttons.
//
// Ready · Set · Go! (#3722, round 2) are Rhythm Echo's words, colours, curve and row (learn_cue.ts,
// learn_cue_row.tsx), on the beats solfegeCueNow puts them: "Ready" with two beats left, "Set" with
// one, "Go!" on the take's first beat. The count-in's dots and number beside the prompt are
// unchanged and count every beat; the words are added to them, not swapped for them. The row takes
// the sing screen's slack between the status line and the meter throughout sing, count-in and take
// alike, word or no word, so nothing moves as a word appears or as the take begins, and nothing
// above the status line moves at all. On a screen too short for the row's minimum the body scrolls
// rather than the word painting over the meter (issue #3559, see learn_cue_row.tsx).
//
// On the listen screen, while the phrase is not playing, a syllable under the staff can be tapped to
// hear its note on its own (onPlayNote); the phase does not change. While the phrase plays, and
// through the count-in, the take and the result, the notes are not buttons.
//
// Presentational: everything is read from the session's state through its selectors, and every
// button is a callback the page dispatches, so a story can render any moment of a phrase from a
// state it drives the reducer to. The page owns the header, as Interval Training's does, and renders
// other screens for the summary and a failure. No lyrics, no key name, no letter names and no
// interval names: the syllables are the only labels.

export const solfegePhraseScreenId = 'solfege-phrase';
// The main button's id names what it does in the phase on screen; it is one element throughout
export const solfegeSingButtonId = 'solfege-sing-button';
export const solfegeStartOverButtonId = 'solfege-start-over-button';
export const solfegeNextButtonId = 'solfege-next-button';
// Listen's "Play again", the phrase once more; not the summary's "Play again" (a new session)
export const solfegeHearAgainButtonId = 'solfege-hear-again-button';
export const solfegeHearMineButtonId = 'solfege-hear-mine-button';
export const solfegeSingAgainButtonId = 'solfege-sing-again-button';
export const solfegeHearGuideButtonId = 'solfege-hear-guide-button';

export const solfegePhraseRowTestId = 'solfege-phrase-row';
export const solfegePipTestId = 'solfege-pip';
export const solfegeTitleTestId = 'solfege-title';
export const solfegeLabelTestId = 'solfege-label';
export const solfegePromptTestId = 'solfege-prompt';
export const solfegeCountInTestId = 'solfege-count-in';
export const solfegeCountInBeatTestId = 'solfege-count-in-beat';
export const solfegeCueTestId = 'solfege-cue';
export const solfegeStatusTestId = 'solfege-status';
export const solfegeHintTestId = 'solfege-hint';
export const solfegeHomeLineTestId = 'solfege-home-line';
export const solfegeSentenceTestId = 'solfege-sentence';
export const solfegeLegendTestId = 'solfege-legend';

export const listenPromptText = 'Listen and follow the syllables';
export const singAlongPromptText = 'Sing along';
export const yourTurnPromptText = 'Your turn';
export const playingPhraseText = 'Playing the phrase';
export const firstNoteText = 'Listen for your first note';
export const guideSoftlyText = 'Guide playing softly';
export const followTheDotText = 'Follow the dot';
export const singItWithMeText = 'Sing it with me';
export const singItText = 'Sing it';
export const playAgainText = 'Play again';
export const startOverText = 'Start over';
export const nextPhraseText = 'Next phrase';
export const seeYourScoreText = 'See your score';
export const hearMineText = 'Hear mine';
export const hearGuideText = 'Hear guide';
export const stopText = 'Stop';
export const singAgainText = 'Sing again';
export const legendTexts = { green: 'on pitch', yellow: 'close', red: 'missed' } as const;

export function phraseCountText(index: number, count: number): string {
  return `Phrase ${index + 1} of ${count}`;
}

// What the count-in says to a screen reader: the beats are drawn, the number is shown
export function countInLabel(beatsLeft: number): string {
  return `Starting in ${beatsLeft}`;
}

interface Callbacks {
  onSing: () => void; // "Sing it with me" / "Sing it"
  onPlayAgain: () => void; // "Play again" on the listen screen
  onStartOver: () => void;
  onNext: () => void; // "Next phrase", or "See your score" after the last
  onHearMine: () => void; // "Hear mine", and "Stop" while it plays
  onHearGuide: () => void; // "Hear guide", and "Stop" while it plays
  onSingAgain: () => void;
}

interface Props extends Callbacks {
  state: SolfegeSessionState; // in listen, sing or result
  clef: Clef; // the singer's, from clefForVoicePart
  canHearMine: boolean; // the page holds a recording of the take to play back
  onPlayNote: (index: number) => void; // a syllable tapped on the listen screen, by its place in the phrase
}

// Nothing sung yet: one array, so the line's memo holds across the listen screen's renders
const noSamples: SolfegeTraceSample[] = [];

export const SolfegePhraseScreen = ({ state, clef, canHearMine, onPlayNote, ...callbacks }: Props) => {
  const { phase } = state;
  const { phrase, homeMidi } = currentSolfegePhrase(state);
  const phraseIndex = solfegePhraseIndex(state);
  const phraseCount = solfegePhraseCount(state);
  const geometry = useSolfegeStaffGeometry(phrase, homeMidi, clef);
  const dot = phase === 'sing' ? solfegeLiveDot(state) : undefined;
  // The samples are one array per trace stack, so the line is worked out only when a sample is added
  // (about every 30 ms of voice), not on every render a frame or a tick causes; only its join to the
  // dot, which moves on each of those, is redone per render (solfege_trace.ts, Cost)
  const samples = phase === 'listen' ? noSamples : solfegeTraceSamples(state);
  const params = solfegeLevelParams[state.level];
  const line = useMemo(() => solfegeTraceLine(samples, geometry, { params }), [samples, geometry, params]);
  // The lead by its numbers: each render's dot is a new object, and the same numbers draw the same line
  const lead = solfegeTraceLead(dot);
  const leadIntoMs = lead?.intoMs;
  const leadMidi = lead?.midi;
  const trace = useMemo(
    () =>
      solfegeTraceJoin(
        line,
        geometry,
        leadIntoMs === undefined || leadMidi === undefined ? undefined : { intoMs: leadIntoMs, midi: leadMidi },
      ),
    [line, geometry, leadIntoMs, leadMidi],
  );
  // A syllable plays its note only on the listen screen with nothing playing (see the header)
  const notesPlayable = phase === 'listen' && !isSolfegePhrasePlaying(state);
  const beatsLeft = solfegeCountInBeatsLeft(state);
  return (
    <ScreenBody
      id={solfegePhraseScreenId}
      data-phase={phase}
      data-phrase-index={phraseIndex}
      data-guide={state.guide}
      data-beats-left={beatsLeft ?? ''}
    >
      <ProgressPipsRow
        label={phraseCountText(phraseIndex, phraseCount)}
        pips={attemptPips(phraseIndex, phraseCount)}
        rowTestId={solfegePhraseRowTestId}
        pipTestId={solfegePipTestId}
      />
      <Title data-testid={solfegeTitleTestId}>{phrase.title}</Title>
      <Label data-testid={solfegeLabelTestId}>{phrase.label}</Label>
      <PromptRow>
        <Prompt data-testid={solfegePromptTestId}>{promptText(state)}</Prompt>
        {beatsLeft !== undefined && <CountIn beatsLeft={beatsLeft} beats={phrase.beatsPerBar} />}
        {phase === 'result' && <HearGuidePill state={state} onHearGuide={callbacks.onHearGuide} />}
      </PromptRow>
      <StaffRow>
        <SolfegeStaff
          clef={clef}
          homeMidi={homeMidi}
          phrase={phrase}
          displays={solfegeNoteDisplays(state)}
          trace={trace}
          dot={dot}
          playheadMs={phase === 'result' ? solfegePlaybackIntoMs(state) : undefined}
          onTapNote={notesPlayable ? onPlayNote : undefined}
        />
      </StaffRow>
      {/* One slot per phase, so the foot after them stays the same element from phase to phase */}
      {phase === 'listen' && <ListenParts state={state} />}
      {phase === 'sing' && <SingParts state={state} />}
      {phase === 'result' && <ResultParts state={state} />}
      <Foot state={state} canHearMine={canHearMine} {...callbacks} />
    </ScreenBody>
  );
};

// The purple line under the label: what to do now, or on the result how it went
function promptText(state: SolfegeSessionState): string {
  if (state.phase === 'listen') {
    return listenPromptText;
  }
  if (state.phase === 'result') {
    return solfegeResultCountText(state);
  }
  return state.guide === 'singAlong' ? singAlongPromptText : yourTurnPromptText;
}

// The count-in: a mark per beat of the bar, the ones still to come filled, and the number left in
// large type, so a singer who cannot count the clicks still sees when to come in
const CountIn = ({ beatsLeft, beats }: { beatsLeft: number; beats: number }) => (
  <CountInBox data-testid={solfegeCountInTestId} data-beats-left={beatsLeft} aria-label={countInLabel(beatsLeft)}>
    <CountInBeats aria-hidden="true">
      {Array.from({ length: beats }, (_beat, beat) => (
        // a beat is counted off from the left: the last `beatsLeft` marks are the ones to come
        <CountInBeat
          key={beat}
          data-testid={solfegeCountInBeatTestId}
          data-left={beat >= beats - beatsLeft ? 'true' : 'false'}
          $left={beat >= beats - beatsLeft}
        />
      ))}
    </CountInBeats>
    <CountInNumber aria-hidden="true">{beatsLeft}</CountInNumber>
  </CountInBox>
);

// "Hear guide", and "Stop" while the phrase plays (see the header)
const HearGuidePill = ({ state, onHearGuide }: { state: SolfegeSessionState; onHearGuide: () => void }) => {
  const playing = isSolfegeHearingGuide(state);
  return (
    <HearGuideButton
      id={solfegeHearGuideButtonId}
      data-testid={solfegeHearGuideButtonId}
      data-playing={playing ? 'true' : 'false'}
      type="button"
      disabled={isSolfegeResultSettling(state)}
      onClick={onHearGuide}
    >
      <HearGuideFace>
        <SpeakerIcon aria-hidden="true" />
        <span>{playing ? stopText : hearGuideText}</span>
      </HearGuideFace>
    </HearGuideButton>
  );
};

const ListenParts = ({ state }: { state: SolfegeSessionState }) => {
  const parts = solfegeHomeLine(currentSolfegePhrase(state).phrase);
  return (
    <>
      <Status data-testid={solfegeStatusTestId}>
        {isSolfegePhrasePlaying(state) && (
          <>
            <SpeakerIcon aria-hidden="true" />
            <span>{playingPhraseText}</span>
          </>
        )}
      </Status>
      <Slack />
      <HomeLine data-testid={solfegeHomeLineTestId}>
        {/* the first do is home itself, in the accent colour as the artboard draws it */}
        <TextRuns parts={parts} homeFirst />
      </HomeLine>
    </>
  );
};

const SingParts = ({ state }: { state: SolfegeSessionState }) => {
  const hint = solfegeHint(state);
  const counting = solfegeCountInBeatsLeft(state) !== undefined;
  const singAlong = state.guide === 'singAlong';
  // the hint goes with a voiced dot: in the take, and in the count-in once it reads the voice
  const hintText = hint && (
    <Hint data-testid={solfegeHintTestId} data-hint={hint}>
      {solfegeHintText(hint)}
    </Hint>
  );
  return (
    <>
      <Status data-testid={solfegeStatusTestId}>
        {counting ? (
          <>
            {/* "Listen for your first note" and a hint beside it are too wide for a small phone's
                line, so in the count-in a hint takes its place (#3722, round 2 review) */}
            <SpeakerIcon aria-hidden="true" />
            {hintText || <span>{firstNoteText}</span>}
          </>
        ) : (
          <>
            {singAlong ? <SpeakerIcon aria-hidden="true" /> : <MicIcon aria-hidden="true" />}
            <span>{singAlong ? guideSoftlyText : followTheDotText}</span>
            {hintText && (
              <>
                <span aria-hidden="true">·</span>
                {hintText}
              </>
            )}
          </>
        )}
      </Status>
      <CueSlack>
        <CueRow cue={solfegeCueNow(state)} testId={solfegeCueTestId} />
      </CueSlack>
      <HearingYouMeter level={solfegeMicLevel(state)} />
    </>
  );
};

const ResultParts = ({ state }: { state: SolfegeSessionState }) => (
  <>
    <Sentence data-testid={solfegeSentenceTestId}>
      <TextRuns parts={solfegeResultSentenceParts(state)} />
    </Sentence>
    <Legend data-testid={solfegeLegendTestId}>
      {(['green', 'yellow', 'red'] as const).map((color) => (
        <LegendItem key={color} data-color={color}>
          <LegendDot style={{ backgroundColor: solfegeResultColorCss[color] }} />
          {legendTexts[color]}
        </LegendItem>
      ))}
    </Legend>
    <Slack />
  </>
);

// Runs of text, the strong ones in bold. A run is known by where it starts in the whole text, which
// is unique and stays put for a given text; `homeFirst` sets the first run, do, in the accent colour.
const TextRuns = ({ parts, homeFirst = false }: { parts: SolfegeTextPart[]; homeFirst?: boolean }) => {
  let offset = 0;
  const runs = parts.map((part) => {
    const run = { ...part, start: offset };
    offset += part.text.length;
    return run;
  });
  return (
    <>
      {runs.map((run) =>
        run.strong ? (
          <Strong key={run.start} $home={homeFirst && run.start === 0}>
            {run.text}
          </Strong>
        ) : (
          <Fragment key={run.start}>{run.text}</Fragment>
        ),
      )}
    </>
  );
};

// What the main button is in each phase: its id, words, look, what a tap does, and its element's
// key - one for listen and sing, another for the result (see the header)
function mainButton(state: SolfegeSessionState, callbacks: Callbacks) {
  if (state.phase === 'listen') {
    return {
      key: 'sing',
      id: solfegeSingButtonId,
      text: state.guide === 'singAlong' ? singItWithMeText : singItText,
      solid: true,
      onClick: callbacks.onSing,
    };
  }
  if (state.phase === 'sing') {
    return {
      key: 'sing',
      id: solfegeStartOverButtonId,
      text: startOverText,
      solid: false,
      onClick: callbacks.onStartOver,
    };
  }
  const last = solfegePhraseIndex(state) >= solfegePhraseCount(state) - 1;
  return {
    key: 'result',
    id: solfegeNextButtonId,
    text: last ? seeYourScoreText : nextPhraseText,
    solid: true,
    onClick: callbacks.onNext,
  };
}

// The buttons: the main one throughout, and under it what the phase adds (see the header)
const Foot = ({
  state,
  canHearMine,
  ...callbacks
}: { state: SolfegeSessionState; canHearMine: boolean } & Callbacks) => {
  const main = mainButton(state, callbacks);
  const hearing = isSolfegeHearingMine(state);
  // the result's first moments: its buttons are not taken yet
  const settling = isSolfegeResultSettling(state);
  return (
    <ScreenFoot>
      <ButtonColumn>
        <ToggleButton
          key={main.key}
          id={main.id}
          data-testid={main.id}
          $solid={main.solid}
          disabled={settling}
          onClick={main.onClick}
        >
          {main.text}
        </ToggleButton>
        {state.phase === 'listen' && (
          <SecondaryButton
            id={solfegeHearAgainButtonId}
            data-testid={solfegeHearAgainButtonId}
            onClick={callbacks.onPlayAgain}
          >
            {playAgainText}
          </SecondaryButton>
        )}
        {state.phase === 'result' && (
          <ButtonRow>
            <SecondaryButton
              id={solfegeHearMineButtonId}
              data-testid={solfegeHearMineButtonId}
              data-playing={hearing ? 'true' : 'false'}
              disabled={!canHearMine || settling}
              onClick={callbacks.onHearMine}
            >
              {hearing ? stopText : hearMineText}
            </SecondaryButton>
            <SecondaryButton
              id={solfegeSingAgainButtonId}
              data-testid={solfegeSingAgainButtonId}
              disabled={settling}
              onClick={callbacks.onSingAgain}
            >
              {singAgainText}
            </SecondaryButton>
          </ButtonRow>
        )}
      </ButtonColumn>
    </ScreenFoot>
  );
};

const Title = styled.div`
  margin-top: 14px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 22px;
  line-height: 30px;

  ${shortScreen} {
    margin-top: 8px;
    font-size: 20px;
    line-height: 26px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-darker);
`;

const PromptRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
  margin-top: 10px;

  ${shortScreen} {
    margin-top: 6px;
  }
`;

const Prompt = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 17px;
  line-height: 24px;
  color: var(--color-tab-selected);
`;

const CountInBox = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CountInBeats = styled.div`
  display: flex;
  gap: 6px;
`;

const CountInBeat = styled.div<{ $left: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $left }) => ($left ? 'var(--color-tab-selected)' : 'var(--color-divider)')};
`;

const CountInNumber = styled.div`
  min-width: 18px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 28px;
  line-height: 28px;
  text-align: center;
  color: var(--color-tab-selected);
`;

// The artboards draw the staff in a 250-tall box whose syllables sit 64 px above its foot; the staff
// draws 24 of those itself, so the row adds the rest under it
const StaffRow = styled.div`
  margin-top: 6px;
  padding-bottom: 40px;

  ${shortScreen} {
    margin-top: 4px;
    padding-bottom: 12px;
  }
`;

// The line under the staff, its height kept while it is empty so nothing under it moves, and one
// line in every phase, never two: a line that wrapped as a hint came and went would move the cue row
// and the meter under it (#3722, round 2 review)
const Status = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 22px;
  white-space: nowrap;
  overflow: hidden;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);

  svg {
    flex: none;
    width: 22px;
    height: 22px;
    color: var(--color-tab-selected);
  }
`;

const Hint = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  color: ${pitchHintAmber};
`;

const HomeLine = styled.div`
  flex: none;
  padding: 14px 16px;
  border-radius: 12px;
  background-color: var(--color-background-search);
  font-size: 14px;
  line-height: 20px;
  color: var(--color-text-darker);

  ${shortScreen} {
    padding: 10px 12px;
  }
`;

const Strong = styled.span<{ $home?: boolean }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  color: ${({ $home }) => ($home ? 'var(--color-tab-selected)' : 'inherit')};
`;

const Sentence = styled.div`
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: var(--color-text-darker);
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 14px;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);

  ${shortScreen} {
    margin-top: 8px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 6px;
`;

// The tap target: 44 px high, pulled back by its margins to the prompt line's 28 px, so the line
// keeps its height and the staff stays put
const HearGuideButton = styled.button`
  flex: none;
  display: flex;
  align-items: center;
  min-height: 44px;
  margin: -8px 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// What is drawn: a soft tinted pill, as the Range Finder's call to action is, a size smaller
const HearGuideFace = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  color: var(--color-tab-selected);
  background-color: color-mix(in srgb, var(--color-tab-selected) 13%, transparent);

  svg {
    flex: none;
    width: 18px;
    height: 18px;
  }

  button:active:not(:disabled) > & {
    background-color: color-mix(in srgb, var(--color-tab-selected) 22%, transparent);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
`;
